import { logger } from 'utils';
import { Store } from 'stores/type';
import { PLAY_NOT_READY } from 'constants/events';
import EventsManager from 'managers/events.manager';

import { EventHandler, WatcherHandler, TrackingMethods } from './types';
import { CUSTOM_DIMENSIONS, CUSTOM_METRICS } from './custom';
import HANDLERS from './handlers';

const isEventHandler = (handler: EventHandler | WatcherHandler): handler is EventHandler =>
  handler.hasOwnProperty('trigger');
const CUSTOM_NAMES = {
  ...CUSTOM_DIMENSIONS,
  ...CUSTOM_METRICS,
};

class AnalyticsManager {
  private $store!: Store;

  public async init(store: Store) {
    this.$store = store;

    document.addEventListener(
      PLAY_NOT_READY,
      () => this.send('metrics', 'not_ready_play_click', 'timing', window.performance.now(), null, true),
    );

    await EventsManager.onReady();
    this.setup();
  }

  public setup = () => {
    this.setupTracker();
    this.initHandlers();
  }
  private setupTracker() {
    if (!window.ga) return;

    window.ga('set', 'transport', 'beacon');
  }
  private initHandlers() {
    const trackingHelpers: TrackingMethods = {
      send: this.send,
      set: this.set,
      timing: this.sendTiming,

      $store: this.$store,
    };
    HANDLERS.forEach((handler) => {
      // @ts-ignore
      const callback = (data: any) => handler.handle(trackingHelpers, data);
      if (isEventHandler(handler)) {
        EventsManager.on(handler.trigger, callback);
      } else {
        this.$store.watch((_state, getters) => handler.watcher(getters), callback);
      }
    });
  }

  public send = (
    category: string,
    action: string,
    label: string | number | null = null,
    value: number | null = null,
    fields = null,
    nonInteraction = false,
  ) => {
    if (!window.ga) return;

    const options: any = {};
    if (nonInteraction) {
      options.nonInteraction = true;
    }

    if (fields === null) {
      window.ga('send', 'event', category, action, label, value, options);
    } else {
      window.ga('send', 'event', category, action, fields, options);
    }

    this.logger('[EVENT]', category, action, label, value, fields, options);
  }
  public set = (name: keyof typeof CUSTOM_NAMES, value: string | boolean | number) => {
    if (!window.ga) return;

    window.ga('set', CUSTOM_NAMES[name], value);
    this.logger('[SET]', name, value);
  }
  public sendTiming = (name: string, value: number) => {
    if (!window.ga) return;

    window.ga('send', 'timing', 'Game', name, value);
    this.logger('[TIMING]', name, value);
  }
  public handleErrors(err: Error) {
    if (!window.ga) return;

    window.ga('send', 'exception', {
      exDescription: err.message || err,
      exFatal: false,
    });
  }

  private logger(...messages: any[]) {
    logger('analytics', ...messages);
  }
}

export { AnalyticsManager };
export default new AnalyticsManager();
